import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import { features } from "../../config/features";
import { QRCodeSVG } from "qrcode.react";
import MasterPage from "../master-page";
import {
  validateSelfie,
  createCaptureSession,
  pollCaptureSession,
  analyzeDocument,
  requestManualReview,
} from "../../actions/verification.action";
import { UPDATE_VERIFICATION_STATUS } from "../../reducers/account.reducer";

// ─── Webcam Modal ─────────────────────────────────────────────────────────────

const WebcamModal = ({ onCapture, onClose, mode = "selfie" }) => {
  const isDoc = mode === "document";
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [captured, setCaptured] = useState(null); // data-URL of snapshot
  const [error, setError] = useState(null);

  // Start camera on mount
  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: isDoc ? "environment" : "user",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err.name === "NotAllowedError"
              ? "Camera access was denied. Please allow camera access in your browser and try again."
              : "Could not access the camera. Make sure it is connected and not in use by another app.",
          );
        }
      }
    };

    start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    setCaptured(canvas.toDataURL("image/jpeg", 0.92));
  }, []);

  const handleRetake = useCallback(() => {
    setCaptured(null);
  }, []);

  const handleUse = useCallback(() => {
    if (!captured) return;
    // Convert data-URL → File
    const byteString = atob(captured.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++)
      ia[i] = byteString.charCodeAt(i);
    const blob = new Blob([ab], { type: "image/jpeg" });
    const file = new File(
      [blob],
      `${isDoc ? "id-doc" : "selfie"}-${Date.now()}.jpg`,
      { type: "image/jpeg" },
    );
    onCapture(file);
  }, [captured, onCapture]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <i
              className={`fal ${isDoc ? "fa-id-card" : "fa-camera"} text-[#9aa82d] text-lg`}
            ></i>
            <h3 className="text-base font-semibold text-gray-900">
              {isDoc ? "Capture ID Document" : "Take a Selfie"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {error ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <i className="fal fa-video-slash text-4xl text-red-400"></i>
              <p className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Video / snapshot area */}
              <div
                className="relative rounded-xl overflow-hidden bg-black"
                style={{ aspectRatio: "16/9" }}
              >
                {/* Live feed */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{
                    display: captured ? "none" : "block",
                    transform: isDoc ? "none" : "scaleX(-1)",
                  }}
                />

                {/* Captured snapshot */}
                {captured && (
                  <img
                    src={captured}
                    alt="captured"
                    className="w-full h-full object-cover"
                    style={{ transform: isDoc ? "none" : "scaleX(-1)" }}
                  />
                )}

                {/* Loading overlay */}
                {!ready && !captured && !error && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    <i className="fas fa-circle-notch fa-spin text-white text-2xl"></i>
                    <span className="text-white text-xs">Starting camera…</span>
                  </div>
                )}

                {/* Face guide overlay on live feed */}
                {ready && !captured && !isDoc && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div
                      className="rounded-full border-2 border-white border-opacity-60"
                      style={{
                        width: "45%",
                        height: "75%",
                        boxShadow: "0 0 0 9999px rgba(0,0,0,0.25)",
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Tip */}
              {!captured && (
                <p className="text-xs text-gray-400 text-center">
                  {isDoc
                    ? "Place your ID document within the rectangle. Ensure good lighting and avoid glare."
                    : "Centre your face in the oval. Ensure good lighting and look straight at the camera."}
                </p>
              )}

              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Buttons */}
              <div className="flex gap-3 justify-center">
                {!captured ? (
                  <button
                    type="button"
                    disabled={!ready}
                    onClick={handleCapture}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#9aa82d] hover:bg-[#7d8a1f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d]"
                  >
                    <i className="fas fa-camera"></i>
                    Capture
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleRetake}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <i className="fal fa-redo"></i>
                      Retake
                    </button>
                    <button
                      type="button"
                      onClick={handleUse}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-[#9aa82d] hover:bg-[#7d8a1f] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d]"
                    >
                      <i className="fas fa-check"></i>
                      Use Photo
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── QR Capture Modal ─────────────────────────────────────────────────────────

const SESSION_TTL_SECONDS = 5 * 60;

const QrCaptureModal = ({ onCapture, onClose, mode = "selfie" }) => {
  const isDoc = mode === "document";
  const dispatch = useDispatch();
  const [token, setToken] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(SESSION_TTL_SECONDS);
  const [status, setStatus] = useState("loading"); // loading | waiting | completed | expired | error
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  const clearTimers = useCallback(() => {
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
  }, []);

  const startSession = useCallback(async () => {
    clearTimers();
    setStatus("loading");
    setError(null);
    setToken(null);
    setQrUrl(null);
    setSecondsLeft(SESSION_TTL_SECONDS);
    try {
      const res = await dispatch(createCaptureSession());
      const sessionToken =
        res?.Result?.Token ?? res?.Result?.token ?? res?.Token ?? res?.token;
      setToken(sessionToken);
      const capturePath = isDoc
        ? "/mobile-capture-document"
        : "/mobile-capture";
      setQrUrl(`${window.location.origin}${capturePath}?token=${sessionToken}`);
      setStatus("waiting");
    } catch (err) {
      setStatus("error");
      setError(err?.message ?? "Failed to create session. Please try again.");
    }
  }, [dispatch, clearTimers]);

  // Start session on mount
  useEffect(() => {
    startSession();
    return () => clearTimers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer — only active while waiting
  useEffect(() => {
    if (status !== "waiting") return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          setStatus("expired");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [status]);

  // Poll every 3 s — only while waiting and token is set
  useEffect(() => {
    if (status !== "waiting" || !token) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await dispatch(pollCaptureSession(token));
        const sessionStatus =
          res?.Result?.Status ??
          res?.Result?.status ??
          res?.Status ??
          res?.status;
        if (sessionStatus === "Completed") {
          clearTimers();
          const imageBase64 =
            res?.Result?.ImageBase64 ??
            res?.Result?.imageBase64 ??
            res?.ImageBase64 ??
            res?.imageBase64;
          if (imageBase64) {
            const byteString = atob(imageBase64);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++)
              ia[i] = byteString.charCodeAt(i);
            const blob = new Blob([ab], { type: "image/jpeg" });
            const file = new File([blob], `mobile-selfie-${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            setStatus("completed");
            onCapture(file);
          }
        }
      } catch (err) {
        console.warn("[QR Poll] error:", err);
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [status, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, "0");
    const s = (secs % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <i className="fal fa-qrcode text-[#9aa82d] text-lg"></i>
            <h3 className="text-base font-semibold text-gray-900">
              {isDoc ? "Scan to Capture ID" : "Scan to Take Selfie"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col items-center gap-5">
          {status === "loading" && (
            <div className="flex flex-col items-center gap-3 py-10">
              <i className="fas fa-circle-notch fa-spin text-3xl text-[#9aa82d]"></i>
              <p className="text-sm text-gray-500">Generating QR code…</p>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <i className="fal fa-exclamation-triangle text-4xl text-red-400"></i>
              <p className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={startSession}
                className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Try Again
              </button>
            </div>
          )}

          {status === "waiting" && qrUrl && (
            <>
              <p className="text-sm text-gray-500 text-center">
                {isDoc
                  ? "Scan this QR code with your phone to capture your ID document."
                  : "Scan this QR code with your phone to take a selfie."}
              </p>
              <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                <QRCodeSVG value={qrUrl} size={200} level="M" />
              </div>
              <div
                className={`flex items-center gap-2 text-sm font-mono font-semibold ${
                  secondsLeft <= 60 ? "text-red-500" : "text-gray-600"
                }`}
              >
                <i className="fal fa-clock"></i>
                <span>{formatTime(secondsLeft)}</span>
              </div>
              <p className="text-xs text-gray-400 text-center">
                Waiting for your phone… The code expires when the timer reaches
                00:00.
              </p>
            </>
          )}

          {status === "expired" && (
            <div className="flex flex-col items-center gap-4 py-10 text-center">
              <i className="fal fa-clock text-4xl text-yellow-400"></i>
              <p className="text-sm text-gray-700 font-medium">
                Session expired.
              </p>
              <button
                type="button"
                onClick={startSession}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-[#9aa82d] hover:bg-[#7d8a1f] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d]"
              >
                <i className="fal fa-sync"></i>
                Generate New Code
              </button>
            </div>
          )}

          {status === "completed" && (
            <div className="flex flex-col items-center gap-3 py-10">
              <i className="fas fa-check-circle text-5xl text-green-500"></i>
              <p className="text-sm text-green-700 font-medium">
                Photo received!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Upload Zone ─────────────────────────────────────────────────────────────

const UploadZone = ({
  label,
  icon,
  file,
  onFile,
  accept = "image/jpeg,image/png,image/tiff,image/bmp,image/heif",
  allowCamera = false,
  captureMode = "selfie",
  showUpload = true,
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) onFile(dropped);
    },
    [onFile],
  );

  const handleCapture = useCallback(
    (file) => {
      onFile(file);
      setShowCamera(false);
    },
    [onFile],
  );

  const handleQrCapture = useCallback(
    (file) => {
      onFile(file);
      setShowQrModal(false);
    },
    [onFile],
  );

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 mb-1">
          {icon && (
            <div className="w-7 h-7 rounded-full bg-[#98a62d] bg-opacity-10 flex items-center justify-center">
              <i className={`${icon} text-[#98a62d] text-xs`}></i>
            </div>
          )}
          <p className="text-sm font-semibold text-gray-800">{label}</p>
        </div>

        {showUpload && (
          <div
            className={`relative w-full border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${
              dragging
                ? "border-[#9aa82d] bg-[#f5f7eb] shadow-md"
                : file
                  ? "border-[#9aa82d] border-opacity-40 bg-white"
                  : "border-gray-300 bg-gray-50 hover:border-[#9aa82d] hover:bg-[#fafbf3] hover:shadow-sm"
            }`}
            style={{ minHeight: "200px" }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            {preview ? (
              <div className="p-3 w-full flex flex-col items-center">
                <img
                  src={preview}
                  alt="preview"
                  className="max-h-56 max-w-full rounded-lg object-contain"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-10 px-6 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <i className="fal fa-cloud-upload text-xl text-gray-400"></i>
                </div>
                <div className="space-y-1">
                  <span className="text-sm text-gray-600 block">
                    Drag & drop or{" "}
                    <span className="text-[#9aa82d] font-semibold cursor-pointer">
                      browse files
                    </span>
                  </span>
                  <span className="text-xs text-gray-400 block">
                    JPG, PNG, WEBP — max 6 MB
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {showUpload && (
          <div className="flex items-center justify-between w-full min-h-[32px]">
            {file ? (
              <div className="flex items-center gap-2 bg-[#f5f7eb] rounded-lg px-3 py-1.5 w-full">
                <i className="fas fa-image text-[#9aa82d] text-xs"></i>
                <span className="text-xs text-gray-700 truncate flex-1">
                  {file.name}
                </span>
                <button
                  type="button"
                  className="text-xs text-red-500 hover:text-red-700 font-medium shrink-0 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFile(null);
                  }}
                >
                  <i className="fal fa-times mr-1"></i>Remove
                </button>
              </div>
            ) : (
              <span className="text-xs text-gray-400 px-1">
                No file selected
              </span>
            )}
          </div>
        )}

        {!showUpload && preview && (
          <div className="w-full border-2 border-[#9aa82d] border-opacity-40 rounded-xl bg-white p-3 flex flex-col items-center">
            <img
              src={preview}
              alt="preview"
              className="max-h-56 max-w-full rounded-lg object-contain"
            />
            <button
              type="button"
              className="mt-2 text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
              onClick={() => onFile(null)}
            >
              <i className="fal fa-times mr-1"></i>Retake
            </button>
          </div>
        )}

        {allowCamera && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCamera(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:border-[#9aa82d] hover:bg-[#fafbf3] transition-all shadow-sm"
              title="Use webcam"
            >
              <i className="fal fa-camera text-[#9aa82d]"></i>
              Use Camera
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowQrModal(true);
              }}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:border-[#9aa82d] hover:bg-[#fafbf3] transition-all shadow-sm"
              title="Use phone camera via QR code"
            >
              <i className="fal fa-mobile-alt text-[#9aa82d]"></i>
              Use Phone
            </button>
          </div>
        )}

        {showUpload && (
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => onFile(e.target.files[0] || null)}
          />
        )}
      </div>

      {showCamera && (
        <WebcamModal
          onCapture={handleCapture}
          onClose={() => setShowCamera(false)}
          mode={captureMode}
        />
      )}

      {showQrModal && (
        <QrCaptureModal
          onCapture={handleQrCapture}
          onClose={() => setShowQrModal(false)}
          mode={captureMode}
        />
      )}
    </>
  );
};

// ─── Quality Badge ────────────────────────────────────────────────────────────

const QualityBadge = ({ isValid }) =>
  isValid ? (
    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold bg-green-50 text-green-700 border border-green-200">
      <i className="fas fa-check-circle"></i> Passed
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold bg-red-50 text-red-600 border border-red-200">
      <i className="fas fa-times-circle"></i> Failed
    </span>
  );

// ─── Selfie Quality Result Panel ──────────────────────────────────────────────

const SelfieQualityPanel = ({ quality }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-600 font-medium">Quality check:</span>
      <QualityBadge isValid={quality.IsValid} />
    </div>

    {quality.RejectionReasons?.length > 0 && (
      <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <i className="fas fa-exclamation-triangle text-red-400 text-sm"></i>
          <p className="text-sm font-semibold text-red-700">Issues detected</p>
        </div>
        <ul className="space-y-1.5 ml-6">
          {quality.RejectionReasons.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-red-600">
              <i className="fas fa-circle text-[4px] mt-2 shrink-0"></i>
              {r}
            </li>
          ))}
        </ul>
      </div>
    )}

    {quality.IsValid && (
      <div className="flex items-center gap-2 bg-[#f5f7eb] border border-green-200 rounded-lg px-4 py-3">
        <i className="fas fa-check-circle text-[#9aa82d]"></i>
        <p className="text-sm text-green-800 font-medium">
          Selfie quality meets all requirements.
        </p>
      </div>
    )}
  </div>
);

// ─── Step Indicator ───────────────────────────────────────────────────────────

const StepIndicator = ({ number, label, active, completed }) => (
  <div className="flex items-center gap-2.5">
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
        completed
          ? "bg-[#9aa82d] text-white"
          : active
            ? "bg-[#9aa82d] bg-opacity-15 text-[#9aa82d] border-2 border-[#9aa82d]"
            : "bg-gray-100 text-gray-400 border border-gray-200"
      }`}
    >
      {completed ? <i className="fas fa-check text-[10px]"></i> : number}
    </div>
    <span
      className={`text-sm ${active || completed ? "font-semibold text-gray-800" : "text-gray-400"}`}
    >
      {label}
    </span>
  </div>
);

// ─── Tab: Selfie Validation ───────────────────────────────────────────────────

const SelfieValidationTab = () => {
  const dispatch = useDispatch();
  const currentStatus = useSelector(
    (state) => state.account.loggedUser?.VerificationStatus ?? 0,
  );
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!selfie) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await dispatch(validateSelfie(selfie));
      const data = res?.Result ?? res;
      setResult(data);
      if (data?.IsValid && data?.KipuUploadSucceeded && currentStatus !== 4) {
        const newStatus = currentStatus === 2 ? 3 : 1; // DocumentSent→AllSent, else→FaceSent
        dispatch({ type: UPDATE_VERIFICATION_STATUS, payload: newStatus });
      }
    } catch (err) {
      setError(
        err?.Result ?? err?.message ?? "An error occurred. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5 sm:space-y-8">
      {/* Instructions — collapsible summary on mobile */}
      <div className="flex items-start gap-2.5 sm:gap-3 bg-[#f5f7eb] border border-[#9aa82d] border-opacity-20 rounded-lg sm:rounded-xl p-3 sm:p-4">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#9aa82d] bg-opacity-10 flex items-center justify-center shrink-0 mt-0.5">
          <i className="fal fa-info-circle text-[#9aa82d] text-xs sm:text-sm"></i>
        </div>
        <div className="space-y-0.5 sm:space-y-1 min-w-0">
          <p className="text-xs sm:text-sm font-semibold text-gray-800">
            Quality Requirements
          </p>
          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
            <span className="sm:hidden">
              Clear face photo, good lighting, look straight at camera.
            </span>
            <span className="hidden sm:inline">
              Upload or capture a selfie photo. It will be checked for: single
              face detected, acceptable blur level, good exposure,
              forward-facing head pose, and no obstructions (masks or
              sunglasses).
            </span>
          </p>
        </div>
      </div>

      {/* Steps indicator — compact on mobile */}
      <div className="flex items-center gap-3 sm:gap-6">
        <StepIndicator
          number={1}
          label="Photo"
          active={!selfie}
          completed={!!selfie}
        />
        <div className="flex-1 h-px bg-gray-200"></div>
        <StepIndicator
          number={2}
          label="Validate"
          active={!!selfie && !result}
          completed={!!result}
        />
        <div className="flex-1 h-px bg-gray-200"></div>
        <StepIndicator
          number={3}
          label="Results"
          active={false}
          completed={!!result}
        />
      </div>

      {/* Upload area — full width on mobile */}
      <div className="sm:max-w-sm">
        <UploadZone
          label="Selfie Photo"
          icon="fal fa-portrait"
          file={selfie}
          onFile={(f) => {
            setSelfie(f);
            setResult(null);
            setError(null);
          }}
          allowCamera
          showUpload={config.VERIFICATION_TEST_MODE}
        />
      </div>

      {/* Submit — full width on mobile */}
      <div>
        <button
          type="button"
          disabled={!selfie || loading}
          onClick={handleSubmit}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-2.5 rounded-lg text-sm font-medium text-white bg-[#9aa82d] hover:bg-[#7d8a1f] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d] shadow-sm"
        >
          {loading ? (
            <>
              <i className="fas fa-circle-notch fa-spin"></i>
              Validating…
            </>
          ) : (
            <>
              <i className="fal fa-shield-check"></i>
              Verify and Send
            </>
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <i className="fas fa-exclamation-triangle text-red-400"></i>
            <p className="text-sm font-semibold text-red-700">
              Validation failed
            </p>
          </div>
          <p className="text-sm text-red-600 ml-6">{error}</p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <i className="fal fa-clipboard-check text-[#9aa82d]"></i>
              <h3 className="text-sm font-semibold text-gray-800">
                Validation Result
              </h3>
            </div>
          </div>
          <div className="p-5">
            <SelfieQualityPanel quality={result} />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Document Status Badge ────────────────────────────────────────────────────

const DocStatusBadge = ({ status }) => {
  const map = {
    Valid: {
      cls: "bg-green-50 text-green-700 border-green-200",
      icon: "fa-check-circle",
      label: "Valid",
    },
    ManualReview: {
      cls: "bg-yellow-50 text-yellow-700 border-yellow-200",
      icon: "fa-exclamation-circle",
      label: "Manual Review",
    },
    Invalid: {
      cls: "bg-red-50 text-red-600 border-red-200",
      icon: "fa-times-circle",
      label: "Invalid",
    },
  };
  const { cls, icon, label } = map[status] ?? map.Invalid;
  return (
    <span
      className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold border ${cls}`}
    >
      <i className={`fas ${icon}`}></i> {label}
    </span>
  );
};

// ─── Extracted Field Row ──────────────────────────────────────────────────────

const FieldRow = ({ label, value, confidence }) => {
  const confPct = confidence != null ? Math.round(confidence * 100) : null;
  const confColor =
    confPct == null
      ? ""
      : confPct >= 80
        ? "text-green-600"
        : confPct >= 60
          ? "text-yellow-600"
          : "text-red-600";

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0">
      <span className="text-sm text-gray-500 font-medium w-40 shrink-0">
        {label}
      </span>
      <span className="text-sm text-gray-900 font-medium flex-1 text-right truncate">
        {value ?? <span className="text-gray-300 italic">—</span>}
      </span>
      {confPct != null && (
        <span
          className={`text-xs font-semibold ml-3 w-12 text-right ${confColor}`}
        >
          {confPct}%
        </span>
      )}
    </div>
  );
};

// ─── Tab: Document Verification ───────────────────────────────────────────────

const DocumentVerificationTab = () => {
  const currentStatus = useSelector(
    (state) => state.account.loggedUser?.VerificationStatus ?? 0,
  );
  const dispatch = useDispatch();
  const [idImage, setIdImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!idImage) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await dispatch(analyzeDocument(idImage));
      const data = res?.Result ?? res;
      setResult(data);
      if (
        data?.Status === "Valid" &&
        data?.KipuUploadSucceeded &&
        currentStatus !== 4
      ) {
        const newStatus = currentStatus === 1 ? 3 : 2; // FaceSent→AllSent, else→DocumentSent
        dispatch({ type: UPDATE_VERIFICATION_STATUS, payload: newStatus });
      }
    } catch (err) {
      setError(
        err?.Result ?? err?.message ?? "An error occurred. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const fields = result?.ExtractedFields;

  const formatDate = (v) => {
    if (!v) return null;
    try {
      const [year, month, day] = v.split("-").map(Number);
      return new Date(year, month - 1, day).toLocaleDateString("en-CA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return v;
    }
  };

  return (
    <div className="space-y-5 sm:space-y-8">
      {/* Instructions — compact on mobile */}
      <div className="flex items-start gap-2.5 sm:gap-3 bg-[#f5f7eb] border border-[#9aa82d] border-opacity-20 rounded-lg sm:rounded-xl p-3 sm:p-4">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#9aa82d] bg-opacity-10 flex items-center justify-center shrink-0 mt-0.5">
          <i className="fal fa-info-circle text-[#9aa82d] text-xs sm:text-sm"></i>
        </div>
        <div className="space-y-0.5 sm:space-y-1 min-w-0">
          <p className="text-xs sm:text-sm font-semibold text-gray-800">
            Document Verification
          </p>
          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
            <span className="sm:hidden">
              Take a photo of your passport or driver's license.
            </span>
            <span className="hidden sm:inline">
              Upload a photo of a government-issued ID (passport or driver's
              license). The system uses Azure Document Intelligence to extract
              structured fields (name, date of birth, expiry, etc.) and
              validates them against business rules.
            </span>
          </p>
        </div>
      </div>

      {/* Steps indicator — compact on mobile */}
      <div className="flex items-center gap-3 sm:gap-6">
        <StepIndicator
          number={1}
          label="Photo"
          active={!idImage}
          completed={!!idImage}
        />
        <div className="flex-1 h-px bg-gray-200"></div>
        <StepIndicator
          number={2}
          label="Analyze"
          active={!!idImage && !result}
          completed={!!result}
        />
        <div className="flex-1 h-px bg-gray-200"></div>
        <StepIndicator
          number={3}
          label="Results"
          active={false}
          completed={!!result}
        />
      </div>

      {/* Upload area — full width on mobile */}
      <div className="sm:max-w-sm">
        <UploadZone
          label="ID Document Photo"
          icon="fal fa-id-card"
          file={idImage}
          onFile={(f) => {
            setIdImage(f);
            setResult(null);
            setError(null);
          }}
          accept="image/jpeg,image/png,image/tiff,image/bmp,image/heif,application/pdf"
          allowCamera
          captureMode="document"
          showUpload={config.VERIFICATION_TEST_MODE}
        />
      </div>

      {/* Submit — full width on mobile */}
      <div>
        <button
          type="button"
          disabled={!idImage || loading}
          onClick={handleSubmit}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 sm:py-2.5 rounded-lg text-sm font-medium text-white bg-[#9aa82d] hover:bg-[#7d8a1f] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d] shadow-sm"
        >
          {loading ? (
            <>
              <i className="fas fa-circle-notch fa-spin"></i>
              Analyzing Document…
            </>
          ) : (
            <>
              <i className="fal fa-file-search"></i>
              Verify and Send
            </>
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <i className="fas fa-exclamation-triangle text-red-400"></i>
            <p className="text-sm font-semibold text-red-700">
              Analysis failed
            </p>
          </div>
          <p className="text-sm text-red-600 ml-6">{error}</p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="fal fa-clipboard-check text-[#9aa82d]"></i>
                <h3 className="text-sm font-semibold text-gray-800">
                  Document Analysis Result
                </h3>
              </div>
              <DocStatusBadge status={result.Status} />
            </div>
          </div>

          <div className="p-5 space-y-6">
            {/* Reasons — only in test mode */}
            {config.VERIFICATION_TEST_MODE && result.Reasons?.length > 0 && (
              <div
                className={`border-l-4 rounded-lg p-4 ${
                  result.Status === "Valid"
                    ? "bg-yellow-50 border-yellow-300"
                    : result.Status === "ManualReview"
                      ? "bg-yellow-50 border-yellow-400"
                      : "bg-red-50 border-red-400"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <i
                    className={`fas fa-exclamation-triangle text-sm ${
                      result.Status === "Invalid"
                        ? "text-red-400"
                        : "text-yellow-500"
                    }`}
                  ></i>
                  <p
                    className={`text-sm font-semibold ${
                      result.Status === "Invalid"
                        ? "text-red-700"
                        : "text-yellow-700"
                    }`}
                  >
                    {result.Status === "Invalid" ? "Issues Found" : "Warnings"}
                  </p>
                </div>
                <ul className="space-y-1.5 ml-6">
                  {result.Reasons.map((r, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-2 text-sm ${
                        result.Status === "Invalid"
                          ? "text-red-600"
                          : "text-yellow-700"
                      }`}
                    >
                      <i className="fas fa-circle text-[4px] mt-2 shrink-0"></i>
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Status message — always shown when not in test mode */}
            {!config.VERIFICATION_TEST_MODE && (
              <div
                className={`flex items-center gap-2 rounded-lg px-4 py-3 ${
                  result.Status === "Valid"
                    ? "bg-[#f5f7eb] border border-green-200"
                    : "bg-red-50 border border-red-200"
                }`}
              >
                <i
                  className={`fas ${result.Status === "Valid" ? "fa-check-circle text-[#9aa82d]" : "fa-times-circle text-red-500"}`}
                ></i>
                <p
                  className={`text-sm font-medium ${result.Status === "Valid" ? "text-green-800" : "text-red-700"}`}
                >
                  {result.Status === "Valid"
                    ? "Document verified successfully."
                    : result.Status === "Invalid"
                      ? "Document verification failed. Please try again."
                      : "Document requires manual review."}
                </p>
              </div>
            )}

            {/* Valid message — test mode only, no reasons */}
            {config.VERIFICATION_TEST_MODE &&
              result.Status === "Valid" &&
              (!result.Reasons || result.Reasons.length === 0) && (
                <div className="flex items-center gap-2 bg-[#f5f7eb] border border-green-200 rounded-lg px-4 py-3">
                  <i className="fas fa-check-circle text-[#9aa82d]"></i>
                  <p className="text-sm text-green-800 font-medium">
                    Document is valid. All required fields extracted with
                    acceptable confidence.
                  </p>
                </div>
              )}

            {/* Extracted fields — test mode only */}
            {config.VERIFICATION_TEST_MODE && fields && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 rounded-full bg-[#9aa82d] bg-opacity-10 flex items-center justify-center">
                    <i className="fal fa-list-ul text-[#9aa82d] text-[10px]"></i>
                  </div>
                  <p className="text-sm font-semibold text-gray-700">
                    Extracted Fields
                  </p>
                  {fields.DocumentType && (
                    <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
                      {fields.DocumentType}
                    </span>
                  )}
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <FieldRow
                    label="First Name"
                    value={fields.FirstName}
                    confidence={fields.FirstNameConfidence}
                  />
                  <FieldRow
                    label="Last Name"
                    value={fields.LastName}
                    confidence={fields.LastNameConfidence}
                  />
                  <FieldRow
                    label="Date of Birth"
                    value={formatDate(fields.DateOfBirth)}
                    confidence={fields.DateOfBirthConfidence}
                  />
                  <FieldRow
                    label="Expiration Date"
                    value={formatDate(fields.DateOfExpiration)}
                    confidence={fields.DateOfExpirationConfidence}
                  />
                  <FieldRow
                    label="Document Number"
                    value={fields.DocumentNumber}
                    confidence={fields.DocumentNumberConfidence}
                  />
                  <FieldRow
                    label="Country"
                    value={fields.Country}
                    confidence={fields.CountryConfidence}
                  />
                  <FieldRow
                    label="Sex"
                    value={fields.Sex}
                    confidence={fields.SexConfidence}
                  />
                  <FieldRow
                    label="Nationality"
                    value={fields.Nationality}
                    confidence={fields.NationalityConfidence}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const TABS = [
  {
    id: "selfie",
    label: "Selfie Validation",
    icon: "fa-portrait",
    description: "Check photo quality",
  },
  {
    id: "document",
    label: "Document Verification",
    icon: "fa-file-certificate",
    description: "Extract & validate ID",
  },
];

export const VerificationPage = () => {
  const navigate = useNavigate();
  const verificationStatus = useSelector(
    (state) => state.account.loggedUser?.VerificationStatus ?? 0,
  );
  const testMode = config.VERIFICATION_TEST_MODE;

  const visibleTabs = useMemo(() => {
    if (testMode) return TABS;
    switch (verificationStatus) {
      case 1: // Face Sent — only document remaining
        return TABS.filter((t) => t.id === "document");
      case 2: // Document Sent — only selfie remaining
        return TABS.filter((t) => t.id === "selfie");
      case 3: // All Sent
      case 4: // Verified
        return [];
      default: // 0 (Not Verified), 5 (Manual Review)
        return TABS.filter((t) => t.id === "selfie" || t.id === "document");
    }
  }, [verificationStatus, testMode]);

  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(visibleTabs[0]?.id ?? "selfie");
  const [manualReviewRequested, setManualReviewRequested] = useState(false);

  const [manualReviewLoading, setManualReviewLoading] = useState(false);
  const handleRequestManualReview = async () => {
    setManualReviewLoading(true);
    try {
      await dispatch(requestManualReview());
      setManualReviewRequested(true);
    } catch {
      setManualReviewLoading(false);
    }
  };

  // Sync activeTab when visible tabs change (e.g., after completing a step)
  useEffect(() => {
    if (
      visibleTabs.length > 0 &&
      !visibleTabs.find((t) => t.id === activeTab)
    ) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  useEffect(() => {
    if (
      !features.identityVerification ||
      (!testMode && (verificationStatus === 3 || verificationStatus === 4))
    ) {
      navigate("/", { replace: true });
    }
  }, [verificationStatus, testMode, navigate]);

  return (
    <MasterPage>
      <div className="space-y-4 sm:space-y-6">
        {/* Page Header — compact on mobile */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-3 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#98a62d] bg-opacity-10 flex items-center justify-center shrink-0">
              <i className="fal fa-shield-check text-[#98a62d] text-base sm:text-lg"></i>
            </div>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-semibold text-gray-900">
                Identity Verification
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">
                Selfie quality and document verification
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden">
          {/* Tab Navigation — pill style on mobile */}
          <div className="border-b border-gray-200 px-3 sm:px-0">
            <nav className="flex gap-1 sm:gap-0 py-2 sm:py-0" aria-label="Tabs">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex-1 inline-flex items-center justify-center gap-2 sm:gap-2.5 px-3 sm:px-6 py-2.5 sm:py-4 text-xs sm:text-sm font-medium rounded-lg sm:rounded-none transition-colors duration-150 ${
                    activeTab === tab.id
                      ? "text-[#9aa82d] bg-[#f5f7eb] sm:bg-transparent"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <div
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-colors ${
                      activeTab === tab.id
                        ? "bg-[#9aa82d] bg-opacity-10"
                        : "bg-gray-100"
                    }`}
                  >
                    <i
                      className={`fal ${tab.icon} text-xs sm:text-sm ${
                        activeTab === tab.id
                          ? "text-[#9aa82d]"
                          : "text-gray-400"
                      }`}
                    ></i>
                  </div>
                  <div className="text-left hidden sm:block">
                    <span className="block">{tab.label}</span>
                    <span
                      className={`block text-xs font-normal ${
                        activeTab === tab.id
                          ? "text-[#9aa82d] text-opacity-70"
                          : "text-gray-400"
                      }`}
                    >
                      {tab.description}
                    </span>
                  </div>
                  <span className="block sm:hidden text-left leading-tight">
                    {tab.label.split(" ")[0]}
                  </span>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#9aa82d] hidden sm:block"></div>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-3 sm:p-8">
            {activeTab === "selfie" && <SelfieValidationTab />}
            {activeTab === "document" && <DocumentVerificationTab />}
          </div>
        </div>

        {/* No camera option */}
        {!manualReviewRequested ? (
          <div className="text-center py-1 sm:py-2">
            <button
              onClick={handleRequestManualReview}
              disabled={manualReviewLoading}
              className="text-xs sm:text-sm text-gray-400 hover:text-gray-600 underline transition-colors disabled:opacity-50"
            >
              {manualReviewLoading
                ? "Requesting..."
                : "I don't have access to a camera"}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 text-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-2 sm:mb-3">
              <i className="fal fa-clock text-yellow-500 text-lg sm:text-xl"></i>
            </div>
            <p className="text-xs sm:text-sm font-medium text-gray-800">
              Manual review requested
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              An administrator will review your account and verify your
              identity.
            </p>
          </div>
        )}
      </div>
    </MasterPage>
  );
};

export default VerificationPage;
